'use client';

import React, { useId, useMemo, useRef } from 'react';
import { motion, useReducedMotion } from 'motion/react';

const DETAILS_TRANSITION_SECONDS = 0.24;
const FOOTER_TOP_PADDING = 12;
import { LucideIcon } from '@skin-seoul/design-system';
import { CLASSNAME, CountryCurrency, Currency, formatCartCurrency, getCartCouponDisplay } from '@skin-seoul/service';
import type { CartCouponResponse, Country } from '@skin-seoul/service';
import { Condition } from '@skin-seoul/utils';

interface PriceCalculatorProps {
  subtotal: number;
  itemCount: number;
  deliveryFee: number;
  totalPrice: number;
  totalTax: number;
  coupons: CartCouponResponse[];
  isFreeShipping?: boolean;
  freeShippingRemaining?: number;
  country: Country;
  isDetailsVisible: boolean;
  onDetailsVisibleChange: (visible: boolean) => void;
}

const CartOrderSummary = ({
  subtotal,
  itemCount,
  deliveryFee,
  totalPrice,
  totalTax,
  coupons,
  isFreeShipping = false,
  freeShippingRemaining,
  country,
  isDetailsVisible,
  onDetailsVisibleChange,
}: PriceCalculatorProps) => {
  const detailsId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const currency = CountryCurrency[country];
  const formattedSubtotal = formatCartCurrency(currency, subtotal);
  const formattedDeliveryFee = formatCartCurrency(currency, deliveryFee);
  const formattedTotalPrice = formatCartCurrency(currency, totalPrice);
  const formattedTotalTax = formatCartCurrency(currency, totalTax);

  const totalCouponDiscount = coupons.reduce((sum, c) => sum + c.discount, 0);
  const preDiscountTotal = subtotal + deliveryFee + totalTax;
  const formattedPreDiscountTotal = formatCartCurrency(currency, preDiscountTotal);
  const hasDiscountSavings = totalCouponDiscount > 0 && preDiscountTotal > totalPrice;
  const hasShippingRemaining =
    !isFreeShipping && deliveryFee > 0 && Number.isFinite(freeShippingRemaining) && (freeShippingRemaining ?? 0) > 0;

  const displayCoupons = useMemo(
    () => coupons.map((coupon) => ({ ...coupon, ...getCartCouponDisplay(coupon, subtotal) })),
    [coupons, subtotal],
  );

  const handleToggleDetails = () => {
    onDetailsVisibleChange(!isDetailsVisible);
  };
  const handleCloseDetails = () => {
    onDetailsVisibleChange(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="flex flex-col">
      <motion.div
        initial={false}
        animate={{
          height: isDetailsVisible ? 'auto' : 0,
          opacity: isDetailsVisible ? 1 : 0,
          marginTop: isDetailsVisible ? -FOOTER_TOP_PADDING : 0,
          marginBottom: isDetailsVisible ? FOOTER_TOP_PADDING : 0,
        }}
        transition={{ duration: shouldReduceMotion ? 0 : DETAILS_TRANSITION_SECONDS, ease: 'easeInOut' }}
        aria-hidden={!isDetailsVisible}
        inert={!isDetailsVisible}
        className="-mx-4 overflow-hidden"
      >
        {/* Escape bubbles from the region's buttons; keep the region semantic instead of inventing an interactive role. */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <div
          id={detailsId}
          role="region"
          aria-label="Order total details"
          className={`flex max-h-[min(35dvh,280px)] flex-col gap-2 overflow-y-auto overscroll-contain bg-generic-0 p-4 rounded-t-lg border ${hasShippingRemaining ? 'border-neutral-50' : 'border-neutral-75'}`}
          data-vaul-no-drag
          onKeyDown={(event) => {
            if (event.key !== 'Escape') {
              return;
            }
            event.stopPropagation();
            handleCloseDetails();
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-paragraph-small font-medium text-neutral-700">Order Details</h3>
            <button
              type="button"
              aria-label="Close order details"
              onClick={handleCloseDetails}
              className="flex size-6 shrink-0 items-center justify-center rounded-sm text-neutral-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
            >
              <LucideIcon name="X" size={24} strokeWidth={1.6} />
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <span
              className={`text-label-small font-medium text-neutral-400 break-words min-w-0 ${CLASSNAME.NOT_TRANSLATE}`}
            >
              Items Total ({itemCount} {itemCount === 1 ? 'item' : 'items'})
            </span>
            <span className={`text-paragraph-small font-medium text-neutral-700 ${CLASSNAME.NOT_TRANSLATE}`}>
              {formattedSubtotal}
            </span>
          </div>
          {displayCoupons.length > 0 &&
            displayCoupons.map((coupon) => (
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1" key={coupon.code}>
                <span
                  className={`text-label-small font-medium text-neutral-400 break-words min-w-0 ${CLASSNAME.NOT_TRANSLATE}`}
                >
                  {coupon.label}
                  {coupon.discountPercent > 0 && (
                    <span className="text-label-small font-medium text-primary-500">
                      {` (${coupon.discountPercent}% off)`}
                    </span>
                  )}
                </span>
                <span className={`text-paragraph-small font-medium text-neutral-700 ${CLASSNAME.NOT_TRANSLATE}`}>
                  -{formatCartCurrency(currency, coupon.discount)}
                </span>
              </div>
            ))}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <span
              className={`text-label-small font-medium text-neutral-400 break-words min-w-0 ${CLASSNAME.NOT_TRANSLATE}`}
            >
              Delivery Fee
            </span>
            {isFreeShipping || deliveryFee === 0 ? (
              <span className={`text-paragraph-small font-medium text-neutral-700 ${CLASSNAME.NOT_TRANSLATE}`}>
                Free
              </span>
            ) : (
              <span className={`text-paragraph-small font-medium text-neutral-700 ${CLASSNAME.NOT_TRANSLATE}`}>
                {formattedDeliveryFee}
              </span>
            )}
          </div>
          <Condition
            expression={totalTax > 0}
            then={
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <span
                  className={`text-label-small font-medium text-neutral-400 break-words min-w-0 ${CLASSNAME.NOT_TRANSLATE}`}
                >
                  Customs & taxes
                </span>
                <span className={`text-paragraph-small font-medium text-neutral-700 ${CLASSNAME.NOT_TRANSLATE}`}>
                  {formattedTotalTax}
                </span>
              </div>
            }
          />
          {hasShippingRemaining && (
            <p className="text-label-small font-medium text-primary-500">
              {formatCartCurrency(currency, freeShippingRemaining ?? 0)} Away from free shipping
            </p>
          )}
        </div>
      </motion.div>
      <div className="flex flex-wrap items-start justify-between gap-x-1 gap-y-2">
        <span className={`text-paragraph-small font-medium text-neutral-700 ${CLASSNAME.NOT_TRANSLATE}`}>
          Order total
        </span>
        <div
          className={`ml-auto flex min-w-0 max-w-full ${currency === Currency.Colombia ? 'flex-col items-end gap-0.5' : 'flex-wrap items-center justify-end gap-x-2 gap-y-0.5'}`}
        >
          {hasDiscountSavings && (
            <span
              className={`text-label-small font-regular text-neutral-400 line-through break-words ${CLASSNAME.NOT_TRANSLATE}`}
            >
              {formattedPreDiscountTotal}
            </span>
          )}
          <button
            ref={triggerRef}
            type="button"
            onClick={handleToggleDetails}
            aria-expanded={isDetailsVisible}
            aria-controls={detailsId}
            aria-label={`${formattedTotalPrice}. ${isDetailsVisible ? 'Hide' : 'View'} order total details`}
            className="flex min-w-0 max-w-full items-center gap-0.5 rounded-sm text-generic-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
          >
            <span className={`min-w-0 break-words text-heading-h6-mobile font-semibold ${CLASSNAME.NOT_TRANSLATE}`}>
              {formattedTotalPrice}
            </span>
            <LucideIcon name="ChevronUp" size={16} className="shrink-0" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartOrderSummary;
